// Game Configuration and Constants
const GameConfig = {
    // Physics Constants
    PHYSICS: {
        FRAME_RATE: 16, // 60fps
        ACCELERATION: 0.4,
        PRE_GAME_ACCELERATION: 0.35,
        MAX_SPEED: 16.0,
        PRE_GAME_MAX_SPEED: 14.0,
        FRICTION: 0.88,
        PRE_GAME_FRICTION: 0.90
    },
    
    // Character Constants
    CHARACTER: {
        BASE_SIZE: 60,
        MAX_SIZE: 300,
        BASE_PERCENTAGE: 100,
        MAX_PERCENTAGE: 500,
        GROWTH_INTERVAL: 3000 // 3 seconds
    },
    
    // Enemy Constants
    ENEMIES: {
        SIZES: {
            badgers: 90,
            sharks: 90,  // Reduced from 150 to make sharks less difficult
            dinosaurs: 120  // Reduced from 180 to make them more reasonable
        },
        EMOJIS: {
            badgers: '🦡',
            sharks: ['🦈', '⚡🦈⚡'],
            dinosaurs: '🦕'
        }
    },
    
    // Powerup Constants
    POWERUPS: {
        SIZE: 25,
        SPAWN_RATE: 8000,
        LIFETIME: 15000,
        MUSHROOM: {
            EMOJI: '🍄', // Red mushroom emoji (will be color-filtered to blue in rendering)
            MAX_ON_SCREEN: 3, // Maximum mushrooms that can be on screen at once
            SPAWN_INTERVAL: 3000, // 3 seconds between each mushroom spawn
            MIN_LEVEL: 1, // Available from start
            BASE_SIZE_REDUCTION: 10, // Base size reduction per mushroom
            BASE_POINTS: 10, // Base points for first mushroom
            COMBO_WINDOW: 5000, // 5 seconds combo window
            COMBO_MULTIPLIER: 2, // Points multiplier for combo
            ADDITIONAL_SHRINK: 10 // Additional shrink % for combo mushrooms
        },
        MICROSCOPE: {
            EMOJI: '🔬',
            SPAWN_CHANCE: 0.15, // 15% chance when spawning powerups
            MIN_LEVEL: 2, // Only spawn from level 2 onwards
            SIZE_RESET: 100, // Reset player to 100% size
            POINTS: 25 // Points awarded for collection
        },
        BOMB: {
            EMOJI: '💣',
            SPAWN_CHANCE: 0.30, // 30% chance when spawning powerups
            MIN_LEVEL: 3, // Only spawn from level 3 onwards
            POINTS: 15, // Points awarded for collection
            EXPLOSION_RADIUS: 300, // Explosion radius in pixels
            MAX_INVENTORY: 1 // Player can only hold 1 bomb at a time
        }
    },
    
    // Bonus Round Constants
    BONUS: {
        TRIGGER_TIMEOUT: 5000,
        BOY_COUNT: 10,
        BOY_LIFETIME: 15000, // Increased to give more time to collect all boys
        SPAWN_INTERVAL: 1000,
        POINTS_PER_BOY: 50,
        TRIGGER_POINTS: 50
    },
    
    // Audio Constants
    AUDIO: {
        BACKGROUND_VOLUME: 0.3,
        POWERUP_VOLUME: 0.8,
        GAME_OVER_VOLUME: 0.8,
        CLICK_VOLUME: 1.0,
        CROSSFADE_STEPS: 20,
        CROSSFADE_INTERVAL: 50
    },
    
    // Screen Constants
    SCREEN: {
        HEADER_HEIGHT: 80,
        FOOTER_HEIGHT: 140
    },
    
    // Level Configuration
    LEVEL_CONFIGS: {
        1: {
            pointsRequired: 0,
            playerSpeed: { max: 18.0, preGame: 16.0 },
            enemies: {
                badgers: { enabled: true, speed: 2, spawnWeight: 1 }
            },
            globalSpawnRate: 1500,
            name: "Getting Started",
            message: "Welcome to Saboteur! Avoid the badgers!"
        },
        2: {
            pointsRequired: 500,
            playerSpeed: { max: 17.0, preGame: 15.0 },
            enemies: {
                badgers: { enabled: true, speed: 4, spawnWeight: 0.6 },
                sharks: { enabled: true, speed: 3, spawnWeight: 0.4 }
            },
            globalSpawnRate: 1200,
            name: "Deep Waters",
            message: "Level up! Sharks join the hunt and everything moves faster!"
        },
        3: {
            pointsRequired: 1000,
            playerSpeed: { max: 18.0, preGame: 16.0 },
            enemies: {
                badgers: { enabled: true, speed: 6, spawnWeight: 0.4 },
                sharks: { enabled: true, speed: 5, spawnWeight: 0.4 },
                dinosaurs: { enabled: true, speed: 4, spawnWeight: 0.2 }
            },
            globalSpawnRate: 1000,
            name: "Jurassic Threat",
            message: "Dinosaurs have arrived! Ancient predators join the hunt!"
        },
        4: {
            pointsRequired: 1500,
            playerSpeed: { max: 19.0, preGame: 17.0 },
            enemies: {
                badgers: { enabled: true, speed: 8, spawnWeight: 0.3 },
                sharks: { enabled: true, speed: 7, spawnWeight: 0.4 },
                dinosaurs: { enabled: true, speed: 6, spawnWeight: 0.3 }
            },
            globalSpawnRate: 800,
            name: "Speed Demon",
            message: "Everything accelerates! Survival becomes critical!"
        },
        5: {
            pointsRequired: 2000,
            playerSpeed: { max: 20.0, preGame: 18.0 },
            enemies: {
                badgers: { enabled: true, speed: 10, spawnWeight: 0.2 },
                sharks: { enabled: true, speed: 9, spawnWeight: 0.4 },
                dinosaurs: { enabled: true, speed: 8, spawnWeight: 0.4 }
            },
            globalSpawnRate: 600,
            name: "Elite Challenge",
            message: "Elite level! Sharks and dinosaurs dominate!"
        },
        6: {
            pointsRequired: 2500,
            playerSpeed: { max: 21.0, preGame: 19.0 },
            enemies: {
                badgers: { enabled: true, speed: 12, spawnWeight: 0.15 },
                sharks: { enabled: true, speed: 11, spawnWeight: 0.35 },
                dinosaurs: { enabled: true, speed: 10, spawnWeight: 0.5 }
            },
            globalSpawnRate: 450,
            name: "Legendary",
            message: "Legendary status! Dinosaurs dominate the battlefield!"
        },
        7: {
            pointsRequired: 3000,
            playerSpeed: { max: 22.0, preGame: 20.0 },
            enemies: {
                badgers: { enabled: true, speed: 15, spawnWeight: 0.1 },
                sharks: { enabled: true, speed: 14, spawnWeight: 0.3 },
                dinosaurs: { enabled: true, speed: 12, spawnWeight: 0.6 }
            },
            globalSpawnRate: 350,
            name: "Apex Predator",
            message: "Maximum chaos! You are among the apex survivors!"
        }
    }
};

// ES6 export for Vite
export default GameConfig;